import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  ElementRef,
  EventEmitter,
  inject,
  Input,
  OnInit,
  Output,
  signal,
  ViewChild,
} from '@angular/core';
import { SvgIconComponent, SvgIcons } from '@ngneat/svg-icon';
import { FormControl, ReactiveFormsModule, Validators } from '@angular/forms';
import { ConnectedPosition, Overlay, OverlayModule } from '@angular/cdk/overlay';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { animationFrameScheduler, auditTime, debounceTime, distinctUntilChanged, fromEvent } from 'rxjs';
import { ScrollingModule } from '@angular/cdk/scrolling';
import { AnimationOptions, LottieComponent } from 'ngx-lottie';
import { EmployeeStatus, Profile } from '@ffd/api-client';
import {
  AvatarsComponent,
  InfiniteScrollListDirective,
  InfiniteScrollListItemDirective,
  InputComponent,
  DynamicVirtualViewportHeightDirective,
} from '@libs/ui-lib';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { TagEvent, TagPusherService } from '@libs/shared-services';
import { SelectedParticipant } from '@libs/collaboration';
import { isPatient, isProvider } from '../../utils';
import { ParticipantListItem, ParticipantListItemType, ParticipantsList } from './search-participants.interface';
/*TODO: Move to inputs*/
const MINIMUM_SEARCH_LENGTH = 3;
const DEBOUNCE_TIME = 300;
const DEFAULT_HEIGHT = 444;

@Component({
  selector: 'lib-search-participants',
  imports: [
    CommonModule,
    SvgIconComponent,
    InputComponent,
    LottieComponent,
    ReactiveFormsModule,
    OverlayModule,
    AvatarsComponent,
    ScrollingModule,
    DynamicVirtualViewportHeightDirective,
    TranslateModule,
    InfiniteScrollListDirective,
    InfiniteScrollListItemDirective,
  ],
  templateUrl: './search-participants.component.html',
  styleUrl: './search-participants.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SearchParticipantsComponent implements OnInit {
  private readonly destroyRef = inject(DestroyRef);
  private readonly overlay = inject(Overlay);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly tagPusherService = inject(TagPusherService);

  public readonly innerControl = new FormControl<string>('');
  public readonly dropdownMaxHeight = signal(DEFAULT_HEIGHT);
  public readonly dropdownHeight = signal(DEFAULT_HEIGHT);
  public readonly prefixIconSrc = signal<SvgIcons | null>(null);
  public readonly rightIconSrc = signal<SvgIcons>('px-24-arrow-down');
  public readonly showcaseMode = signal(false);
  public readonly showcaseEmailError = signal(false);
  public readonly dropdownOpen = signal(false);

  public readonly inviteLabelText = signal('');
  public readonly noResultLabelText = signal('');
  public readonly placeholderText = signal('');
  public readonly hintText = signal('');
  public readonly errorHintText = signal('');
  public readonly labelText = signal('');
  public readonly closeDropdownOnSelect = signal(false);
  public readonly isLoading = signal(false);
  public readonly isDisabled = signal(false);
  public readonly dropdownItems = signal<ParticipantsList | null>(null);
  public readonly inputSize = signal<'normal' | 'large'>('normal');

  public readonly ParticipantListItemType = ParticipantListItemType;
  public readonly EmployeeStatus = EmployeeStatus;

  public readonly TagEvent = TagEvent;
  public readonly scrollStrategy = this.overlay.scrollStrategies.noop();

  public readonly overlayPositionStrategy: ConnectedPosition[] = [
    {
      originX: 'start',
      originY: 'bottom',
      overlayX: 'start',
      overlayY: 'top',
      offsetY: 4,
    },
    {
      originX: 'start',
      originY: 'top',
      overlayX: 'start',
      overlayY: 'bottom',
    },
  ];

  public lottieOptions: AnimationOptions = {
    path: '/assets/animations/loading-spinner.json',
  };

  @ViewChild('dropdownList', { static: false, read: ElementRef }) public dropdownElement!: ElementRef<HTMLDivElement>;
  @ViewChild('dropdownControl', { static: true, read: ElementRef }) public dropdownControlElement!: ElementRef<HTMLDivElement>;
  @ViewChild(InputComponent) public inputComponent!: InputComponent;

  public translateService = inject(TranslateService);

  @Input() public currentUser?: Profile | null = null;
  @Input() public allParticipants: SelectedParticipant[] = [];

  @Input() public set maxHeight(value: number) {
    this.dropdownMaxHeight.set(value);
  }

  @Input() public set prefixIcon(value: SvgIcons | null) {
    this.prefixIconSrc.set(value);
  }

  @Input() public set placeholder(value: string) {
    this.placeholderText.set(value);
  }

  @Input() public set hint(value: string) {
    this.hintText.set(value);
  }

  @Input() public set label(value: string) {
    this.labelText.set(value);
  }

  @Input() public set isShowcaseMode(value: boolean) {
    this.showcaseMode.set(value);
    if (value) {
      this.innerControl.setValidators([Validators.email, Validators.pattern('^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\\.[a-z]{2,4}$')]);
    } else {
      this.innerControl.removeValidators([Validators.email, Validators.pattern('^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\\.[a-z]{2,4}$')]);
    }
  }

  @Input() public set loading(value: boolean) {
    this.isLoading.set(value);
  }

  @Input() public set disabled(value: boolean) {
    this.isDisabled.set(value);
    if (value) {
      this.innerControl.disable();
    } else {
      this.innerControl.enable();
    }
  }

  @Input() public set closeOnSelect(value: boolean) {
    this.closeDropdownOnSelect.set(value);
  }

  @Input() public set options(value: ParticipantsList | undefined) {
    this.setDropdownHeight(this.getDropdownContainerHeight());
    this.dropdownItems.set(value ?? null);
  }

  @Input() public set noResultLabel(value: string) {
    this.noResultLabelText.set(value);
  }

  @Input() public set inviteLabel(value: string) {
    this.inviteLabelText.set(value);
  }

  @Input() public minimumSearchLength = MINIMUM_SEARCH_LENGTH;

  @Input() public set size(value: 'normal' | 'large') {
    this.inputSize.set(value);
  }

  @Input() public isError: boolean = false;

  @Input() public set errorHint(value: string) {
    this.errorHintText.set(value);
  }

  @Input() public isPatientSearch: boolean = false;

  @Output() public readonly searchTerm = new EventEmitter<string | null>();
  @Output() public readonly itemSelected = new EventEmitter<ParticipantListItem>();
  @Output() public readonly endReached = new EventEmitter<void>();

  protected isProvider = isProvider;
  protected isPatient = isPatient;

  public ngOnInit(): void {
    this.watchWindowResize();
    this.innerControl.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef), debounceTime(DEBOUNCE_TIME), distinctUntilChanged())
      .subscribe((searchTerm) => {
        if (!this.showcaseMode()) {
          this.openDropdown();
          this.searchTerm.next(searchTerm);
        }
      });
    if (this.isPatientSearch) {
      this.tagPusherService.pushTag({ event: TagEvent.PatientSearchOpened });
    }
  }

  public onIconClick(event: MouseEvent): void {
    if (this.showcaseMode()) return;
    event?.stopPropagation();
    this.dropdownOpen.update((open) => !open);
    this.rightIconSrc.update((icon) => (icon === 'px-24-arrow-down' ? 'px-24-arrow-up' : 'px-24-arrow-down'));
  }

  public onOverlayOutsideClick(event: MouseEvent): void {
    const clickTarget = event.target as HTMLElement;
    const isFilterCloseButtonClicked = clickTarget.closest('.dropdown-button');
    const isInputFieldClicked = this.dropdownControlElement.nativeElement.contains(clickTarget);

    if (isFilterCloseButtonClicked || isInputFieldClicked) {
      return;
    }
    event.preventDefault();
    this.closeDropdown();
  }

  public getDropdownContainerHeight(): number {
    const rect = this.dropdownControlElement.nativeElement.getBoundingClientRect().bottom;
    const windowHeight = window.innerHeight;
    return windowHeight - rect;
  }

  public watchWindowResize(): void {
    fromEvent(window, 'resize')
      .pipe(distinctUntilChanged(), auditTime(0, animationFrameScheduler), takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.setDropdownHeight(this.getDropdownContainerHeight());
      });
  }

  public setDropdownHeight(height: number): void {
    this.dropdownHeight.set(height);
    this.cdr.markForCheck();
  }

  public openDropdown(): void {
    if (
      this.dropdownOpen() ||
      this.showcaseMode() ||
      this.isDisabled() ||
      !this.innerControl.value ||
      (this.innerControl.value && this.innerControl.value?.length < MINIMUM_SEARCH_LENGTH)
    ) {
      return;
    }
    this.setDropdownHeight(this.getDropdownContainerHeight());
    this.rightIconSrc.set('px-24-arrow-up');
    this.dropdownOpen.set(true);
  }

  public closeDropdown(): void {
    this.rightIconSrc.set('px-24-arrow-down');
    this.dropdownOpen.set(false);
  }

  public selectItem(item: ParticipantListItem): void {
    this.itemSelected.emit(item);
    this.innerControl.reset();

    if (this.closeDropdownOnSelect()) {
      this.closeDropdown();
    }
  }

  public addShowcaseParticipant(): void {
    this.isError = false;

    if (!this.showcaseMode() || !this.innerControl.value) {
      return;
    }

    if (this.innerControl.value.toLocaleLowerCase() === this.currentUser?.email) {
      this.errorHintText.set(this.translateService.instant('collab_new_collab_modal_collaboration_showcase_own_email_error'));
      this.showcaseEmailError.set(true);
      return;
    }

    if (this.allParticipants.map((e) => e.email?.toLocaleLowerCase()).includes(this.innerControl.value.toLocaleLowerCase())) {
      this.errorHintText.set(this.translateService.instant('collab_new_collab_modal_collaboration_showcase_duplicated_email_error'));
      this.showcaseEmailError.set(true);
      return;
    }
    if (this.innerControl.value && this.innerControl.valid) {
      this.itemSelected.emit({
        id: crypto.getRandomValues(new Uint32Array(1))[0].toString(),
        pmsEmail: this.innerControl.value,
        type: ParticipantListItemType.Participant,
        status: EmployeeStatus.NOT_INVITED,
      });
      this.showcaseEmailError.set(false);
      this.innerControl.reset();
    } else {
      this.errorHintText.set(this.translateService.instant('collab_new_collab_modal_collaboration_showcase_email_error'));
      this.showcaseEmailError.set(true);
    }
  }

  public onEndReached(): void {
    this.endReached.emit();
  }

  public focusSearchInput(): void {
    this.inputComponent.focus();
  }
}
